import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, MessageSquare, Check } from "lucide-react";
import { api } from "@/lib/api";
import { notifText } from "@/lib/notifText";

const ago = (iso) => {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
};

export const NotificationsSection = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ["notifications"], queryFn: () => api.get("/notifications").then((r) => r.data), refetchInterval: 60000 });
  const unread = items.filter((n) => !n.read);
  if (!items.length) return null;
  const markAll = async () => { await api.post("/notifications/read", {}); qc.invalidateQueries({ queryKey: ["notifications"] }); };
  const open = async (n) => {
    if (!n.read) await api.post("/notifications/read", { ids: [n.id] });
    qc.invalidateQueries({ queryKey: ["notifications"] });
    navigate(`/u/${n.universe_id}/lore/${n.entity_id}?comment=${n.comment_id}`);
  };
  return (
    <section className="mb-10 rounded-md border border-[#2D3442] bg-[#13171F] p-5" data-testid="notifications-section">
      <div className="mb-3 flex items-center gap-2">
        <Bell className="h-4 w-4 text-[#F59E0B]" />
        <span className="lf-label flex-1">Notifications{unread.length ? ` · ${unread.length} new` : ""}</span>
        {unread.length > 0 && <button onClick={markAll} className="flex items-center gap-1 text-xs text-[#F59E0B] hover:underline" data-testid="notifications-mark-all"><Check className="h-3.5 w-3.5" />Mark all read</button>}
      </div>
      <ul className="divide-y divide-[#2D3442]/60">
        {items.slice(0, 8).map((n) => (
          <li key={n.id}>
            <button onClick={() => open(n)} className="flex w-full items-start gap-3 py-2.5 text-left hover:bg-[#1A202C]/50" data-testid={`notification-${n.id}`}>
              <span className="relative mt-0.5"><MessageSquare className="h-4 w-4 text-[#A8A090]" />{!n.read && <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-[#EF4444]" data-testid={`notification-unread-${n.id}`} />}</span>
              <span className="min-w-0 flex-1">
                <span className={`block text-sm ${n.read ? "text-[#A8A090]" : "text-[#F3EBDD]"}`}>
                  <b className="font-medium">{n.from_name}</b> {notifText(n.kind)} on <span className="text-[#F59E0B]">{n.entity_name}</span>
                  <span className="text-[#A8A090]"> in {n.universe_name}</span>
                </span>
                <span className="block truncate text-xs italic text-[#A8A090]">“{n.snippet}”</span>
              </span>
              <span className="shrink-0 text-[10px] text-[#A8A090]">{ago(n.created_at)}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};
